import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"

import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { adminReportPath, adminReportsPath } from "@/routes"

interface ReportItem {
  id: number
  status: "pending" | "reviewed" | "resolved" | "dismissed"
  reason: string
  reported_type: string
  description: string | null
  reporter: { id: number; name: string }
  moderator: { id: number; name: string } | null
  created_at: string
  reviewed_at: string | null
}

interface Props {
  reports: ReportItem[]
  stats: {
    pending: number
    reviewed: number
    resolved: number
    dismissed: number
  }
  filters: {
    status?: string
    reason?: string
    type?: string
  }
}

export default function AdminReportsIndex({ reports, stats, filters }: Props) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const statusLabel: Record<string, string> = {
    pending: "Pendiente",
    reviewed: "En revisión",
    resolved: "Resuelto",
    dismissed: "Descartado",
  }

  const reasonLabel: Record<string, string> = {
    spam: "Spam",
    inappropriate: "Inapropiado",
    harassment: "Acoso",
    fake: "Falso",
    scam: "Estafa",
    dangerous: "Peligroso",
    other: "Otro",
  }

  const handleFilterChange = (key: string, value: string) => {
    router.get(
      adminReportsPath(),
      { ...filters, [key]: value || undefined },
      { preserveState: true },
    )
  }

  return (
    <AppLayout>
      <Head title="Reportes - Panel Admin" />

      <div className="mx-auto max-w-7xl space-y-6 p-4">
        <div className="mb-4">
          <Link href="/admin">
            <Button variant="ghost" size="sm" className="mb-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver al panel
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Reportes de contenido</h1>
          <p className="text-muted-foreground">
            Gestión de reportes de usuarios
          </p>
        </div>

        <div className="mb-4 grid w-full grid-cols-2 gap-4 md:grid-cols-4">
          <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
            <div className="text-primary text-[32px] font-bold">
              {stats.pending}
            </div>
            <div className="text-muted-foreground text-[9px] font-bold">
              PENDIENTES
            </div>
          </div>
          <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
            <div className="text-primary text-[32px] font-bold">
              {stats.reviewed}
            </div>
            <div className="text-muted-foreground text-[9px] font-bold">
              EN REVISIÓN
            </div>
          </div>
          <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
            <div className="text-primary text-[32px] font-bold">
              {stats.resolved}
            </div>
            <div className="text-muted-foreground text-[9px] font-bold">
              RESUELTOS
            </div>
          </div>
          <div className="bg-card overflow-hidden rounded-[11px] p-3 text-center">
            <div className="text-primary text-[32px] font-bold">
              {stats.dismissed}
            </div>
            <div className="text-muted-foreground text-[9px] font-bold">
              DESCARTADOS
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-muted-foreground text-xs font-medium">
              Estado
            </label>
            <select
              value={filters.status ?? ""}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="border-input bg-card rounded-md border px-3 py-2 pr-8 text-sm"
            >
              <option value="">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="reviewed">En revisión</option>
              <option value="resolved">Resuelto</option>
              <option value="dismissed">Descartado</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-muted-foreground text-xs font-medium">
              Razón
            </label>
            <select
              value={filters.reason ?? ""}
              onChange={(e) => handleFilterChange("reason", e.target.value)}
              className="border-input bg-card rounded-md border px-3 py-2 pr-8 text-sm"
            >
              <option value="">Todas</option>
              <option value="spam">Spam</option>
              <option value="inappropriate">Inapropiado</option>
              <option value="harassment">Acoso</option>
              <option value="fake">Falso</option>
              <option value="scam">Estafa</option>
              <option value="dangerous">Peligroso</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-muted-foreground text-xs font-medium">
              Tipo de contenido
            </label>
            <select
              value={filters.type ?? ""}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              className="border-input bg-card rounded-md border px-3 py-2 pr-8 text-sm"
            >
              <option value="">Todos</option>
              <option value="ForumPost">Post</option>
              <option value="ForumAnswer">Respuesta</option>
              <option value="Event">Evento</option>
              <option value="User">Usuario</option>
              <option value="Business">Negocio</option>
              <option value="Product">Producto</option>
              <option value="Raffle">Sorteo</option>
              <option value="Interview">Entrevista</option>
              <option value="Announcement">Anuncio</option>
              <option value="ChatMessage">Mensaje</option>
            </select>
          </div>
        </div>

        <div>
          {reports.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No hay reportes con los filtros seleccionados
            </p>
          ) : (
            <div className="divide-y">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between px-6 py-4"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="border-border bg-muted text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                        {statusLabel[report.status]}
                      </span>
                      <span className="border-border bg-muted text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                        {reasonLabel[report.reason] ?? report.reason}
                      </span>
                      <span className="border-border bg-muted text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                        {report.reported_type}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Reportado por{" "}
                      <span className="font-medium">
                        {report.reporter.name}
                      </span>
                      {" · "}
                      {formatDate(report.created_at)}
                    </p>
                    {report.description && (
                      <p className="text-muted-foreground line-clamp-1 text-xs">
                        {report.description}
                      </p>
                    )}
                  </div>
                  <Link
                    href={adminReportPath(report.id)}
                    className="text-primary ml-4 shrink-0 text-sm hover:underline"
                  >
                    Ver detalles →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
